// PopupImageManagement.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FiSearch,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiFilter,
  FiArrowUp,
  FiArrowDown,
  FiCalendar,
  FiCheck,
  FiX,
  FiImage,
  FiUpload,
  FiFolder,
  FiGlobe,
  FiToggleLeft,
  FiToggleRight,
  FiAlertCircle,
} from "react-icons/fi";
import {
  FaRegImage,
  FaRegCheckCircle,
  FaRegTimesCircle,
  FaProjectDiagram,
} from "react-icons/fa";

const PopupImageManagement = () => {
  const [popupImages, setPopupImages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterActive, setFilterActive] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });
  const [processing, setProcessing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [deactivateInfo, setDeactivateInfo] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    project_id: "",
    is_active: 1,
    image: null,
  });

  const API_BASE = "https://dev.mithahomes.com/backend/api/popmodelimage";
  const Imageurl = "https://dev.mithahomes.com/backend";
  const POPUP_API = `${API_BASE}/popup-image`;

  // Updated API endpoints
  const PROJECTS_API = `https://dev.mithahomes.com/backend/api/mainprojectprocess/projects/sampletes`;

  // Statistics
  const stats = {
    total: Array.isArray(popupImages) ? popupImages.length : 0,
    active: Array.isArray(popupImages)
      ? popupImages.filter(
          (img) => img && (img.is_active === 1 || img.is_active === true),
        ).length
      : 0,
    inactive: Array.isArray(popupImages)
      ? popupImages.filter(
          (img) => img && !(img.is_active === 1 || img.is_active === true),
        ).length
      : 0,
    recent: Array.isArray(popupImages)
      ? popupImages.filter((img) => {
          if (!img || !img.created_at) return false;
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          try {
            return new Date(img.created_at) > weekAgo;
          } catch (e) {
            return false;
          }
        }).length
      : 0,
  };

  // Check if there's an active image for the selected project
  const checkExistingActiveImage = (projectId, excludeId = null) => {
    if (!projectId) return null;

    return popupImages.find(
      (img) =>
        img &&
        img.project_id == projectId &&
        (img.is_active === 1 || img.is_active === true) &&
        img.id !== excludeId,
    );
  };

  // Fetch all popup images
  const fetchPopupImages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(POPUP_API);
      if (response.data && response.data.success) {
        setPopupImages(
          Array.isArray(response.data.data) ? response.data.data : [],
        );
      } else {
        setPopupImages([]);
        toast.error("❌ Failed to load popup images");
      }
    } catch (error) {
      console.error("Error fetching popup images:", error);
      toast.error("❌ Error loading popup images");
      setPopupImages([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all projects from the new API endpoint
  const fetchProjects = async () => {
    try {
      setProjectsLoading(true);
      const response = await axios.get(PROJECTS_API);

      let projectsData = [];

      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.data)
      ) {
        projectsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        projectsData = response.data;
      } else if (response.data && Array.isArray(response.data.projects)) {
        projectsData = response.data.projects;
      }

      // Transform data to match expected structure
      const formattedProjects = projectsData
        .map((project) => {
          return {
            pcid:
              project.pid ||
              project.pcid ||
              project.id ||
              project.project_id ||
              project.ID,
            project_name:
              project.project_name ||
              project.name ||
              project.projectName ||
              "Unnamed Project",
            ...project,
          };
        })
        .filter((project) => project.pcid && project.project_name);

      setProjects(formattedProjects);

      if (formattedProjects.length === 0) {
        toast.warning("⚠️ No projects found. Please add projects first.");
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("❌ Error loading projects");
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  useEffect(() => {
    fetchPopupImages();
    fetchProjects();
  }, []);

  // Get project name by ID
  const getProjectName = (projectId) => {
    if (!projectId && projectId !== 0) return "No Project Assigned";

    const project = projects.find(
      (p) =>
        p && (p.pcid == projectId || p.pid == projectId || p.id == projectId),
    );
    return project && project.project_name
      ? project.project_name
      : `Project #${projectId}`;
  };

  // Filter popup images
  const filteredImages = popupImages.filter((image) => {
    if (!image || typeof image !== "object") return false;

    const projectName = getProjectName(image.project_id);
    const searchTermLower = searchTerm.toLowerCase();

    const projectNameLower = String(projectName || "").toLowerCase();
    const imageIdStr = String(image.id || "").toLowerCase();

    const matchesSearch =
      projectNameLower.includes(searchTermLower) ||
      imageIdStr.includes(searchTermLower);

    const matchesActive =
      filterActive === "all" ||
      (filterActive === "active" &&
        (image.is_active === 1 || image.is_active === true)) ||
      (filterActive === "inactive" &&
        !(image.is_active === 1 || image.is_active === true));

    return matchesSearch && matchesActive;
  });

  // Sort popup images
  const sortedImages = React.useMemo(() => {
    if (!Array.isArray(filteredImages) || !sortConfig.key)
      return filteredImages || [];

    return [...filteredImages].sort((a, b) => {
      if (!a || !b) return 0;

      if (sortConfig.key === "created_at") {
        try {
          const dateA = a[sortConfig.key]
            ? new Date(a[sortConfig.key])
            : new Date(0);
          const dateB = b[sortConfig.key]
            ? new Date(b[sortConfig.key])
            : new Date(0);
          return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
        } catch (e) {
          return 0;
        }
      }

      if (sortConfig.key === "id") {
        const idA = a.id || 0;
        const idB = b.id || 0;
        return sortConfig.direction === "asc" ? idA - idB : idB - idA;
      }

      if (sortConfig.key === "is_active") {
        const aActive = a.is_active === 1 || a.is_active === true;
        const bActive = b.is_active === 1 || b.is_active === true;
        if (aActive && !bActive) return sortConfig.direction === "asc" ? -1 : 1;
        if (!aActive && bActive) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      }

      if (sortConfig.key === "project_id") {
        const projectNameA = getProjectName(a.project_id) || "";
        const projectNameB = getProjectName(b.project_id) || "";
        if (projectNameA < projectNameB) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (projectNameA > projectNameB) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      }

      const valueA = a[sortConfig.key] || "";
      const valueB = b[sortConfig.key] || "";
      if (valueA < valueB) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (valueA > valueB) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredImages, sortConfig, projects]);

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked ? 1 : 0,
    }));
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("❌ Please select an image file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error("❌ Image size should be less than 5MB");
        return;
      }

      setFormData((prev) => ({
        ...prev,
        image: file,
      }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Trigger file input click
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      project_id: "",
      is_active: 1,
      image: null,
    });
    setEditingImage(null);
    setImagePreview(null);
    setDeactivateInfo(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Validate form before submission
  const validateForm = () => {
    if (!formData.project_id) {
      toast.error("❌ Please select a project");
      return false;
    }

    if (!editingImage && !formData.image) {
      toast.error("❌ Please upload an image");
      return false;
    }

    return true;
  };

  // Deactivate existing active image for a project
  const deactivateExistingImage = async (projectId, currentImageId = null) => {
    const existingActive = checkExistingActiveImage(projectId, currentImageId);

    if (existingActive) {
      try {
        // Deactivate the existing active image
        await axios.put(
          `${POPUP_API}/${existingActive.id}`,
          {
            is_active: 0,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        return true;
      } catch (error) {
        console.error("Error deactivating existing image:", error);
        return false;
      }
    }
    return true; // No existing active image
  };

  // Show confirmation modal for deactivation
  const showDeactivationConfirmation = (projectId, currentImageId = null) => {
    const existingActive = checkExistingActiveImage(projectId, currentImageId);

    if (existingActive) {
      setDeactivateInfo({
        projectId,
        currentImageId,
        existingImage: existingActive,
        projectName: getProjectName(projectId),
      });
      setShowDeactivateConfirm(true);
      return false;
    }
    return true;
  };

  // ✅ Add new popup image or update existing
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setProcessing(true);

      // Get selected project details
      const selectedProject = projects.find(
        (p) => p.pcid == formData.project_id,
      );
      if (!selectedProject) {
        toast.error("❌ Selected project not found");
        return;
      }

      // If setting as active and there's an existing active image, show confirmation
      if (formData.is_active === 1) {
        const existingActive = checkExistingActiveImage(
          formData.project_id,
          editingImage?.id,
        );

        if (existingActive) {
          // Ask for confirmation before proceeding
          if (
            !window.confirm(
              `⚠️ Warning!\n\nThere is already an active popup image for "${getProjectName(formData.project_id)}" (Image #${existingActive.id}).\n\nSetting this image as active will automatically deactivate the existing one.\n\nDo you want to continue?`,
            )
          ) {
            setProcessing(false);
            return;
          }

          // Deactivate existing active image
          const deactivated = await deactivateExistingImage(
            formData.project_id,
            editingImage?.id,
          );
          if (!deactivated) {
            toast.error("❌ Failed to deactivate existing active image");
            setProcessing(false);
            return;
          }
        }
      }

      const formDataToSend = new FormData();

      // Send project_id (not pid) and is_active
      formDataToSend.append("project_id", formData.project_id); // Changed from pid to project_id
      formDataToSend.append("is_active", formData.is_active);

      // Append the uploaded popup image
      if (formData.image instanceof File) {
        formDataToSend.append("image", formData.image);
      } else if (editingImage && editingImage.image) {
        // If editing and no new image, keep the existing one
        // Note: For PUT request, we need to handle this differently
        // The backend will use the existing image if no new file is provided
      }

      let response;
      if (editingImage) {
        // Update popup image - use PUT with FormData
        response = await axios.put(
          `${POPUP_API}/${editingImage.id}`,
          formDataToSend,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        );
      } else {
        // Create new popup image - use POST with FormData
        response = await axios.post(POPUP_API, formDataToSend, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      if (response.data && response.data.success) {
        toast.success(
          `✅ ${editingImage ? "Popup image updated" : "Popup image added"} successfully!`,
        );

        // Show specific message if an image was deactivated
        if (formData.is_active === 1) {
          const existingActive = checkExistingActiveImage(
            formData.project_id,
            editingImage?.id,
          );
          if (existingActive) {
            toast.info(
              `ℹ️ Previous active image (#${existingActive.id}) has been deactivated.`,
            );
          }
        }

        setShowModal(false);
        resetForm();
        fetchPopupImages();
      } else {
        throw new Error(response.data?.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error saving popup image:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error saving popup image";
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setProcessing(false);
    }
  };

  // ✅ Edit popup image
  const handleEdit = (image) => {
    if (!image) return;

    setEditingImage(image);
    setFormData({
      project_id: image.project_id || "",
      is_active: image.is_active === 1 || image.is_active === true ? 1 : 0,
      image: null,
    });
    setImagePreview(image.image ? `${Imageurl}${image.image}` : null);

    // Check if there's already an active image for this project (excluding current)
    const existingActive = checkExistingActiveImage(image.project_id, image.id);
    if (existingActive) {
      toast.warning(
        `⚠️ Another image (#${existingActive.id}) is currently active for this project. 
        Activating this image will deactivate the other one.`,
        { autoClose: 5000 },
      );
    }

    setShowModal(true);
  };

  // View popup image details
  const handleView = (image) => {
    if (!image) return;

    setViewingImage(image);
    setShowViewModal(true);
  };

  // ✅ Toggle active status with automatic deactivation
  const toggleActive = async (id, currentActive) => {
    if (!id) return;

    const image = popupImages.find((img) => img && img.id === id);
    if (!image) return;

    const newActive = currentActive ? 0 : 1;
    const projectId = image.project_id;
    const imageId = image.id;

    // If activating, check for existing active image
    if (newActive === 1) {
      const existingActive = checkExistingActiveImage(projectId, imageId);

      if (existingActive) {
        // Ask for confirmation
        if (
          !window.confirm(
            `⚠️ Warning!\n\nThere is already an active popup image for "${getProjectName(projectId)}" (Image #${existingActive.id}).\n\nActivating this image will automatically deactivate the existing one.\n\nDo you want to continue?`,
          )
        ) {
          return;
        }

        // Deactivate existing active image first
        try {
          await axios.put(
            `${POPUP_API}/${existingActive.id}`,
            {
              is_active: 0,
            },
            {
              headers: {
                "Content-Type": "application/json",
              },
            },
          );

          toast.info(`ℹ️ Image #${existingActive.id} has been deactivated.`);
        } catch (error) {
          console.error("Error deactivating existing image:", error);
          toast.error("❌ Failed to deactivate existing active image");
          return;
        }
      }
    }

    try {
      // Update the current image's active status
      const response = await axios.put(
        `${POPUP_API}/${id}`,
        {
          is_active: newActive,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data && response.data.success) {
        toast.success(
          `📝 Popup image ${newActive ? "activated" : "deactivated"}!`,
        );
        fetchPopupImages();
      } else {
        throw new Error(response.data?.message || "Update failed");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error updating status";
      toast.error(`❌ ${errorMessage}`);
    }
  };

  // ✅ Delete popup image
  const handleDelete = async (id) => {
    if (!id) return;

    if (
      window.confirm(
        "Are you sure you want to delete this popup image? This action cannot be undone.",
      )
    ) {
      try {
        const response = await axios.delete(`${POPUP_API}/${id}`);

        if (response.data && response.data.success) {
          toast.success("🗑️ Popup image deleted successfully!");
          fetchPopupImages();
        } else {
          throw new Error(response.data?.message || "Delete failed");
        }
      } catch (error) {
        console.error("Error deleting popup image:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Error deleting popup image";
        toast.error(`❌ ${errorMessage}`);
      }
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Unknown date";

    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return "Invalid date";
    }
  };

  // Get status color and icon
  const getStatusConfig = (isActive) => {
    const active = isActive === 1 || isActive === true;
    if (active) {
      return {
        background: "#d1fae5",
        color: "#065f46",
        border: "#a7f3d0",
        icon: <FaRegCheckCircle />,
        text: "Active",
      };
    } else {
      return {
        background: "#fee2e2",
        color: "#dc2626",
        border: "#fecaca",
        icon: <FaRegTimesCircle />,
        text: "Inactive",
      };
    }
  };

  // Get project icon based on type
  const getProjectIcon = (projectId) => {
    if (!projectId && projectId !== 0) {
      return (
        <FaProjectDiagram style={{ color: "#9ca3af", fontSize: "1.2rem" }} />
      );
    }

    const project = projects.find(
      (p) =>
        p && (p.pcid == projectId || p.pid == projectId || p.id == projectId),
    );
    if (project && project.project_name) {
      const name = project.project_name.toLowerCase();
      if (name.includes("residential") || name.includes("home")) {
        return <FiFolder style={{ color: "#667eea", fontSize: "1.2rem" }} />;
      } else if (name.includes("commercial") || name.includes("office")) {
        return <FiGlobe style={{ color: "#10b981", fontSize: "1.2rem" }} />;
      }
    }
    return (
      <FaProjectDiagram style={{ color: "#f59e0b", fontSize: "1.2rem" }} />
    );
  };

  // Get active image count for a project
  const getActiveCountForProject = (projectId) => {
    return popupImages.filter(
      (img) =>
        img &&
        img.project_id == projectId &&
        (img.is_active === 1 || img.is_active === true),
    ).length;
  };

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "20px",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "30px",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "2.5rem",
              fontWeight: "700",
              color: "#1e293b",
              margin: "0 0 8px 0",
            }}
          >
            Popup Images
          </h1>
          <p
            style={{
              fontSize: "1.1rem",
              color: "#64748b",
              margin: "0",
            }}
          >
            Manage popup images for projects (Only one active image per project)
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            border: "none",
            padding: "12px 24px",
            borderRadius: "12px",
            fontSize: "1rem",
            fontWeight: "600",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
            transition: "all 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = "0 8px 25px rgba(102, 126, 234, 0.4)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 15px rgba(102, 126, 234, 0.3)";
          }}
        >
          <FiPlus style={{ fontSize: "1.1rem" }} />
          Add New Popup Image
        </button>
      </div>

      {/* Information Banner */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          border: "1px solid #f59e0b",
          borderRadius: "12px",
          padding: "15px 20px",
          marginBottom: "25px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <FiAlertCircle
          style={{ color: "#d97706", fontSize: "1.5rem", flexShrink: 0 }}
        />
        <div>
          <p
            style={{
              margin: "0",
              color: "#92400e",
              fontWeight: "500",
            }}
          >
            <strong>Important:</strong> Each project can have only one active
            popup image at a time. Activating a new image will automatically
            deactivate any previously active image for the same project.
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        {/* Total Popup Images */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-4px)";
            e.target.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #667eea, #764ba2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FaRegImage />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.total}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Total Images
            </p>
          </div>
        </div>

        {/* Active Images */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-4px)";
            e.target.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #10b981, #059669)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FiCheck />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.active}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Active Images
            </p>
            <p
              style={{
                fontSize: "0.8rem",
                color: "#10b981",
                margin: "4px 0 0 0",
                fontWeight: "500",
              }}
            >
              {projects.length > 0
                ? `Across ${new Set(popupImages.filter((img) => img.is_active === 1).map((img) => img.project_id)).size} projects`
                : "Loading..."}
            </p>
          </div>
        </div>

        {/* Inactive Images */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-4px)";
            e.target.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #ef4444, #dc2626)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FiX />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.inactive}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Inactive Images
            </p>
          </div>
        </div>

        {/* Recent Images (Last 7 days) */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-4px)";
            e.target.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #f59e0b, #d97706)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FiCalendar />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.recent}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Recent (7 days)
            </p>
          </div>
        </div>
      </div>

      {/* Controls Bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "20px",
          marginBottom: "25px",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            position: "relative",
            flex: "1",
            minWidth: "300px",
          }}
        >
          <FiSearch
            style={{
              position: "absolute",
              left: "15px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#94a3b8",
              fontSize: "1.1rem",
            }}
          />
          <input
            type="text"
            placeholder="Search by project name or image ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 20px 12px 45px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "1rem",
              background: "white",
              transition: "all 0.3s ease",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#667eea";
              e.target.style.boxShadow = "0 0 0 3px rgba(102, 126, 234, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e2e8f0";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
          }}
        >
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
            style={{
              padding: "12px 16px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "1rem",
              background: "white",
              cursor: "pointer",
              minWidth: "140px",
            }}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button
            onClick={() => {
              setSearchTerm("");
              setFilterActive("all");
            }}
            style={{
              padding: "12px 16px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "1rem",
              background: "white",
              cursor: "pointer",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.3s ease",
            }}
            onMouseOver={(e) => {
              e.target.style.background = "#f8fafc";
              e.target.style.color = "#475569";
            }}
            onMouseOut={(e) => {
              e.target.style.background = "white";
              e.target.style.color = "#64748b";
            }}
          >
            <FiX /> Clear Filters
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        }}
      >
        {loading ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 40px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid #f1f5f9",
                borderTop: "4px solid #667eea",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            ></div>
            <p
              style={{
                color: "#64748b",
                fontSize: "1rem",
                margin: "0",
              }}
            >
              Loading popup images...
            </p>
          </div>
        ) : !Array.isArray(sortedImages) || sortedImages.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 40px",
              color: "#64748b",
            }}
          >
            <FiImage
              style={{
                fontSize: "4rem",
                color: "#cbd5e1",
                marginBottom: "20px",
              }}
            />
            <h3
              style={{
                fontSize: "1.5rem",
                color: "#475569",
                margin: "0 0 12px 0",
              }}
            >
              No popup images found
            </h3>
            <p
              style={{
                fontSize: "1rem",
                margin: "0 0 25px 0",
              }}
            >
              Try adjusting your search or filter criteria
            </p>
            <button
              onClick={() => setShowModal(true)}
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "12px",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                margin: "0 auto",
              }}
            >
              <FiPlus /> Add Your First Popup Image
            </button>
          </div>
        ) : (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("id")}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    ID
                    {sortConfig.key === "id" &&
                      (sortConfig.direction === "asc" ? (
                        <FiArrowUp />
                      ) : (
                        <FiArrowDown />
                      ))}
                  </span>
                </th>
                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Image Preview
                </th>
                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("project_id")}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    Project
                    {sortConfig.key === "project_id" &&
                      (sortConfig.direction === "asc" ? (
                        <FiArrowUp />
                      ) : (
                        <FiArrowDown />
                      ))}
                  </span>
                </th>
                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("is_active")}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    Status
                    {sortConfig.key === "is_active" &&
                      (sortConfig.direction === "asc" ? (
                        <FiArrowUp />
                      ) : (
                        <FiArrowDown />
                      ))}
                  </span>
                </th>
                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("created_at")}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    Created Date
                    {sortConfig.key === "created_at" &&
                      (sortConfig.direction === "asc" ? (
                        <FiArrowUp />
                      ) : (
                        <FiArrowDown />
                      ))}
                  </span>
                </th>
                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedImages.map((image) => {
                if (!image || !image.id) return null;

                const statusConfig = getStatusConfig(image.is_active);
                const projectName = getProjectName(image.project_id);
                const activeCount = getActiveCountForProject(image.project_id);
                const isOnlyActive = image.is_active === 1 && activeCount === 1;
                const hasMultipleActive = activeCount > 1;

                return (
                  <tr
                    key={image.id}
                    style={{
                      transition: "background 0.3s ease",
                      background: hasMultipleActive ? "#fef3c7" : "white",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = hasMultipleActive
                        ? "#fde68a"
                        : "#f8fafc";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = hasMultipleActive
                        ? "#fef3c7"
                        : "white";
                    }}
                  >
                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "'Roboto Mono', monospace",
                          fontWeight: "600",
                          color: "#475569",
                          fontSize: "0.9rem",
                        }}
                      >
                        #{image.id}
                      </span>
                    </td>

                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          width: "80px",
                          height: "60px",
                          borderRadius: "8px",
                          overflow: "hidden",
                          background: "#f1f5f9",
                          cursor: "pointer",
                        }}
                        onClick={() => handleView(image)}
                      >
                        {image.image ? (
                          <img
                            src={
                              image.image.startsWith("http")
                                ? image.image
                                : `${Imageurl}${image.image}`
                            }
                            alt="Popup preview"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              transition: "transform 0.3s ease",
                            }}
                            onMouseOver={(e) => {
                              e.target.style.transform = "scale(1.1)";
                            }}
                            onMouseOut={(e) => {
                              e.target.style.transform = "scale(1)";
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#94a3b8",
                            }}
                          >
                            <FiImage />
                          </div>
                        )}
                      </div>
                      {hasMultipleActive && (
                        <div
                          style={{
                            marginTop: "5px",
                            fontSize: "0.7rem",
                            color: "#d97706",
                            fontWeight: "600",
                            textAlign: "center",
                          }}
                        >
                          ⚠️ Multiple Active
                        </div>
                      )}
                    </td>

                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        {getProjectIcon(image.project_id)}
                        <div style={{ flex: "1" }}>
                          <h4
                            style={{
                              fontWeight: "600",
                              color: "#1e293b",
                              margin: "0 0 4px 0",
                              fontSize: "1rem",
                            }}
                          >
                            {projectName}
                          </h4>
                          <p
                            style={{
                              color: "#94a3b8",
                              fontSize: "0.875rem",
                              margin: "0",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            Project ID: {image.project_id || "N/A"}
                            {activeCount > 0 && (
                              <span
                                style={{
                                  marginLeft: "8px",
                                  padding: "2px 6px",
                                  background: image.is_active
                                    ? "#10b981"
                                    : "#e2e8f0",
                                  color: image.is_active ? "white" : "#64748b",
                                  borderRadius: "4px",
                                  fontSize: "0.7rem",
                                  fontWeight: "600",
                                }}
                              >
                                {activeCount} active
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 12px",
                          borderRadius: "20px",
                          fontSize: "0.8rem",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          background: statusConfig.background,
                          color: statusConfig.color,
                          border: `1px solid ${statusConfig.border}`,
                          position: "relative",
                        }}
                      >
                        {statusConfig.icon}
                        {statusConfig.text}
                        {isOnlyActive && (
                          <span
                            style={{
                              position: "absolute",
                              top: "-5px",
                              right: "-5px",
                              width: "10px",
                              height: "10px",
                              background: "#10b981",
                              borderRadius: "50%",
                              border: "2px solid white",
                            }}
                          ></span>
                        )}
                      </div>
                    </td>

                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          color: "#64748b",
                          fontSize: "0.875rem",
                        }}
                      >
                        <FiCalendar style={{ fontSize: "0.9rem" }} />
                        {formatDate(image.created_at)}
                      </div>
                    </td>

                    <td
                      style={{
                        padding: "20px",
                        borderBottom: "1px solid #f1f5f9",
                        verticalAlign: "top",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                        }}
                      >
                        <button
                          onClick={() => handleView(image)}
                          style={{
                            background: "#3b82f6",
                            color: "white",
                            border: "none",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                          title="View Details"
                        >
                          <FiEye />
                        </button>
                        <button
                          onClick={() =>
                            toggleActive(image.id, image.is_active)
                          }
                          style={{
                            background: image.is_active ? "#f59e0b" : "#10b981",
                            color: "white",
                            border: "none",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                          title={image.is_active ? "Deactivate" : "Activate"}
                        >
                          {image.is_active ? (
                            <FiToggleLeft />
                          ) : (
                            <FiToggleRight />
                          )}
                        </button>
                        <button
                          onClick={() => handleEdit(image)}
                          style={{
                            background: "#f1f5f9",
                            color: "#475569",
                            border: "1px solid #e2e8f0",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                          title="Edit"
                        >
                          <FiEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(image.id)}
                          style={{
                            background: "#ef4444",
                            color: "white",
                            border: "none",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: "1000",
            padding: "20px",
            backdropFilter: "blur(5px)",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              width: "100%",
              maxWidth: "500px",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
              animation: "modalSlideIn 0.3s ease",
            }}
          >
            <div
              style={{
                padding: "25px 30px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <h2
                style={{
                  margin: "0",
                  fontSize: "1.5rem",
                  fontWeight: "600",
                }}
              >
                {editingImage ? "Edit Popup Image" : "Add New Popup Image"}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  fontSize: "1.5rem",
                  color: "white",
                  cursor: "pointer",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.3s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.3)";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.2)";
                }}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: "30px" }}>
              {/* Project Selection */}
              <div style={{ marginBottom: "25px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#374151",
                    fontSize: "0.95rem",
                  }}
                >
                  Select Project *
                </label>
                <select
                  name="project_id"
                  value={formData.project_id}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "1rem",
                    background: "white",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(102, 126, 234, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e2e8f0";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  <option value="">Select a project...</option>
                  {projectsLoading ? (
                    <option value="" disabled>
                      Loading projects...
                    </option>
                  ) : Array.isArray(projects) && projects.length > 0 ? (
                    projects.map((project) => {
                      if (!project || !project.pcid || !project.project_name)
                        return null;

                      const activeCount = getActiveCountForProject(
                        project.pcid,
                      );
                      return (
                        <option key={project.pcid} value={project.pcid}>
                          {project.project_name} (ID: {project.pcid})
                          {activeCount > 0 &&
                            ` - ${activeCount} active image(s)`}
                        </option>
                      );
                    })
                  ) : (
                    <option value="" disabled>
                      No projects available. Please add projects first.
                    </option>
                  )}
                </select>

                {/* Warning about existing active image */}
                {formData.project_id &&
                  (() => {
                    const existingActive = checkExistingActiveImage(
                      formData.project_id,
                      editingImage?.id,
                    );
                    if (existingActive && formData.is_active === 1) {
                      return (
                        <div
                          style={{
                            marginTop: "10px",
                            padding: "10px",
                            background: "#fef3c7",
                            border: "1px solid #f59e0b",
                            borderRadius: "8px",
                            fontSize: "0.85rem",
                            color: "#92400e",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginBottom: "5px",
                            }}
                          >
                            <FiAlertCircle />
                            <strong>Warning:</strong>
                          </div>
                          <p style={{ margin: "0" }}>
                            There is already an active popup image for this
                            project (Image #{existingActive.id}). Setting this
                            image as active will automatically deactivate the
                            existing one.
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}

                <p
                  style={{
                    margin: "8px 0 0 0",
                    color: "#94a3b8",
                    fontSize: "0.85rem",
                  }}
                >
                  Only one active popup image per project. Setting a new active
                  image will deactivate the previous one.
                </p>
              </div>

              {/* Image Upload */}
              <div style={{ marginBottom: "25px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#374151",
                    fontSize: "0.95rem",
                  }}
                >
                  {editingImage ? "Update Image" : "Upload Image *"}
                </label>

                {/* Image Preview */}
                {(imagePreview || (editingImage && editingImage.image)) && (
                  <div
                    style={{
                      marginBottom: "15px",
                      textAlign: "center",
                    }}
                  >
                    <img
                      src={imagePreview || (editingImage && editingImage.image)}
                      alt="Preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "200px",
                        borderRadius: "12px",
                        border: "2px solid #e2e8f0",
                      }}
                    />
                  </div>
                )}

                {/* Upload Area */}
                <div
                  onClick={triggerFileInput}
                  style={{
                    border: "2px dashed #cbd5e1",
                    borderRadius: "12px",
                    padding: "30px 20px",
                    textAlign: "center",
                    cursor: "pointer",
                    background: "#f8fafc",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.background = "#f1f5f9";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = "#cbd5e1";
                    e.target.style.background = "#f8fafc";
                  }}
                >
                  <FiUpload
                    style={{
                      fontSize: "2.5rem",
                      color: "#94a3b8",
                      marginBottom: "10px",
                    }}
                  />
                  <p
                    style={{
                      margin: "0 0 10px 0",
                      color: "#475569",
                      fontWeight: "500",
                    }}
                  >
                    {formData.image
                      ? "Click to change image"
                      : "Click to upload image"}
                  </p>
                  <p
                    style={{
                      margin: "0",
                      color: "#94a3b8",
                      fontSize: "0.85rem",
                    }}
                  >
                    PNG, JPG, JPEG up to 5MB
                  </p>
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  style={{ display: "none" }}
                />

                {formData.image instanceof File && (
                  <p
                    style={{
                      margin: "10px 0 0 0",
                      color: "#10b981",
                      fontSize: "0.85rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <FiCheck /> {formData.image.name} (
                    {(formData.image.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>

              {/* Active Status */}
              <div style={{ marginBottom: "25px" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    cursor: "pointer",
                    marginBottom: "8px",
                  }}
                >
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active === 1}
                    onChange={handleCheckboxChange}
                    style={{
                      transform: "scale(1.2)",
                    }}
                  />
                  <span
                    style={{
                      fontWeight: "600",
                      color: "#374151",
                      fontSize: "0.95rem",
                    }}
                  >
                    Set as active popup image
                  </span>
                </label>
                <p
                  style={{
                    margin: "8px 0 0 0",
                    color: "#94a3b8",
                    fontSize: "0.85rem",
                  }}
                >
                  When active, this image will be displayed as the popup for the
                  selected project. Setting a new active image will
                  automatically deactivate the previous one.
                </p>
              </div>

              {/* Form Actions */}
              <div
                style={{
                  paddingTop: "25px",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  gap: "15px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  style={{
                    background: "transparent",
                    color: "#64748b",
                    border: "2px solid #e2e8f0",
                    padding: "12px 25px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "1rem",
                    fontWeight: "500",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = "#f8fafc";
                    e.target.style.color = "#475569";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = "transparent";
                    e.target.style.color = "#64748b";
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  style={{
                    background: processing
                      ? "#94a3b8"
                      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    padding: "12px 30px",
                    borderRadius: "10px",
                    cursor: processing ? "not-allowed" : "pointer",
                    fontSize: "1rem",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                    boxShadow: processing
                      ? "none"
                      : "0 4px 15px rgba(102, 126, 234, 0.3)",
                    opacity: processing ? 0.7 : 1,
                  }}
                  onMouseOver={(e) => {
                    if (!processing) {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow =
                        "0 8px 25px rgba(102, 126, 234, 0.4)";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!processing) {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow =
                        "0 4px 15px rgba(102, 126, 234, 0.3)";
                    }
                  }}
                >
                  {processing
                    ? "Processing..."
                    : editingImage
                      ? "Update Image"
                      : "Add Image"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showViewModal && viewingImage && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: "1000",
            padding: "20px",
            backdropFilter: "blur(5px)",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              width: "100%",
              maxWidth: "600px",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
              animation: "modalSlideIn 0.3s ease",
            }}
          >
            <div
              style={{
                padding: "25px 30px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <h2
                style={{
                  margin: "0",
                  fontSize: "1.5rem",
                  fontWeight: "600",
                }}
              >
                Popup Image Details
              </h2>
              <button
                onClick={() => setShowViewModal(false)}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  fontSize: "1.5rem",
                  color: "white",
                  cursor: "pointer",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.3s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.3)";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.2)";
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: "30px" }}>
              {/* Image Display */}
              <div
                style={{
                  textAlign: "center",
                  marginBottom: "25px",
                }}
              >
                <img
                  src={
                    viewingImage.image
                      ? viewingImage.image.startsWith("http")
                        ? viewingImage.image
                        : `${Imageurl}${viewingImage.image}`
                      : ""
                  }
                  alt="Popup full view"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "300px",
                    borderRadius: "16px",
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.parentElement.innerHTML =
                      '<div style="padding: 50px; text-align: center; color: #94a3b8;"><FiImage style="font-size: 3rem; margin-bottom: 10px;" /><p>Image not available</p></div>';
                  }}
                />
              </div>

              {/* Status Badge */}
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 16px",
                  borderRadius: "20px",
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  background: getStatusConfig(viewingImage.is_active)
                    .background,
                  color: getStatusConfig(viewingImage.is_active).color,
                  border: `1px solid ${getStatusConfig(viewingImage.is_active).border}`,
                  marginBottom: "25px",
                }}
              >
                {getStatusConfig(viewingImage.is_active).icon}
                {getStatusConfig(viewingImage.is_active).text}
              </div>

              {/* Details Grid */}
              <div
                style={{
                  background: "#f8fafc",
                  borderRadius: "12px",
                  padding: "20px",
                  marginBottom: "25px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "20px",
                  }}
                >
                  <div>
                    <h4
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        margin: "0 0 8px 0",
                      }}
                    >
                      Image ID
                    </h4>
                    <p
                      style={{
                        fontSize: "1.2rem",
                        fontWeight: "600",
                        color: "#1e293b",
                        margin: "0",
                        fontFamily: "'Roboto Mono', monospace",
                      }}
                    >
                      #{viewingImage.id || "N/A"}
                    </p>
                  </div>

                  <div>
                    <h4
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        margin: "0 0 8px 0",
                      }}
                    >
                      Project
                    </h4>
                    <p
                      style={{
                        fontSize: "1rem",
                        fontWeight: "600",
                        color: "#1e293b",
                        margin: "0",
                      }}
                    >
                      {getProjectName(viewingImage.project_id)}
                    </p>
                    <p
                      style={{
                        fontSize: "0.85rem",
                        color: "#94a3b8",
                        margin: "4px 0 0 0",
                      }}
                    >
                      Project ID: {viewingImage.project_id || "N/A"}
                    </p>
                  </div>

                  <div>
                    <h4
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        margin: "0 0 8px 0",
                      }}
                    >
                      Created Date
                    </h4>
                    <p
                      style={{
                        fontSize: "1rem",
                        fontWeight: "500",
                        color: "#1e293b",
                        margin: "0",
                      }}
                    >
                      {formatDate(viewingImage.created_at)}
                    </p>
                  </div>

                  <div>
                    <h4
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        margin: "0 0 8px 0",
                      }}
                    >
                      URL
                    </h4>
                    <p
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: "500",
                        color: "#3b82f6",
                        margin: "0",
                        wordBreak: "break-all",
                      }}
                    >
                      {viewingImage.image || "No URL available"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "center",
                }}
              >
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEdit(viewingImage);
                  }}
                  style={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "1rem",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flex: "1",
                  }}
                >
                  <FiEdit /> Edit Image
                </button>

                <button
                  onClick={() => setShowViewModal(false)}
                  style={{
                    background: "transparent",
                    color: "#64748b",
                    border: "2px solid #e2e8f0",
                    padding: "12px 24px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "1rem",
                    fontWeight: "500",
                    transition: "all 0.3s ease",
                    flex: "1",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-50px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default PopupImageManagement;
